import { create } from 'zustand';
import { io } from 'socket.io-client';
// MQTT client deprecated - all communication now uses Socket.IO
// import { publishStreamSubscribe, publishStreamUnsubscribe, publishSnapshotRequest } from '../utils/mqttClient';

let liveSocket = null;

// Helper to get mime type from codec
const getContentType = (codec, contentType) => {
  if (contentType) return contentType;
  switch (codec) {
    case 'h264': return 'video/mp4';
    case 'webp': return 'image/webp';
    case 'jpeg':
    default: return 'image/jpeg';
  }
};

export const useLiveStreamStore = create((set, get) => ({
  connected: false,
  activeStreams: {}, // cameraId -> { subscribed, frame, status, fps, resolution, codec, stats }
  streamErrors: {},
  codecCapabilities: {
    h264: typeof window !== 'undefined' && window.MediaSource?.isTypeSupported?.('video/mp4; codecs="avc1.42E01E"'),
    webp: true,
    jpeg: true,
  },

  connect: () => {
    if (liveSocket?.connected) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    liveSocket = io('/live', {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    liveSocket.on('connect', () => {
      set({ connected: true });
      console.log('Live stream socket connected');

      // Report codec capabilities to server
      const { codecCapabilities } = get();
      liveSocket.emit('client:codecs', codecCapabilities);
    });

    liveSocket.on('disconnect', () => {
      set({ connected: false });
      console.log('Live stream socket disconnected');
    });

    liveSocket.on('connect_error', (err) => {
      console.error('Live stream connect error:', err.message);
    });

    liveSocket.io.on('reconnect', () => {
      console.log('Live stream socket reconnected');
      // Re-subscribe to active streams
      const { activeStreams } = get();
      Object.keys(activeStreams).forEach((cameraId) => {
        if (activeStreams[cameraId].subscribed) {
          liveSocket.emit('stream:subscribe', { cameraId });
        }
      });
      // Re-report codec capabilities
      const { codecCapabilities } = get();
      liveSocket.emit('client:codecs', codecCapabilities);
    });

    // Handle subscription confirmation
    liveSocket.on('stream:subscribed', ({ cameraId, cameraName, deviceStatus }) => {
      set((state) => ({
        activeStreams: {
          ...state.activeStreams,
          [cameraId]: {
            ...state.activeStreams[cameraId],
            subscribed: true,
            cameraName,
            deviceStatus,
            status: deviceStatus === 'online' ? 'connecting' : 'device_offline',
          },
        },
      }));
    });

    // Handle incoming frames (from local AI device or remote edge device)
    // Now supports H.264, WebP, and JPEG codecs
    liveSocket.on('stream:frame', ({ cameraId, frame, codec, contentType, timestamp, metadata, stats, source, deviceId }) => {
      console.log(`[STREAM-FRAME-FRONTEND] Received frame for camera ${cameraId} (codec=${codec}, size=${frame?.length || 0} chars)`);

      const actualCodec = codec || 'jpeg';
      const mimeType = getContentType(actualCodec, contentType);

      // For H.264, store raw base64 data (no data URL prefix needed for jmuxer)
      // For image codecs, create data URL for img tag
      const frameData = actualCodec === 'h264'
        ? frame  // Raw base64 for jmuxer
        : `data:${mimeType};base64,${frame}`;

      console.log(`[STREAM-UPDATE] Updating stream state for camera ${cameraId}`);

      set((state) => ({
        activeStreams: {
          ...state.activeStreams,
          [cameraId]: {
            ...state.activeStreams[cameraId],
            frame: frameData,
            codec: actualCodec,
            contentType: mimeType,
            timestamp,
            metadata,
            stats, // Compression stats from encoder
            source: source || 'local', // 'local' or 'edge'
            deviceId,
            status: 'streaming',
            lastFrameTime: Date.now(),
          },
        },
      }));
    });

    // Handle stream status updates
    liveSocket.on('stream:status', ({ cameraId, status, fps, resolution, codec, source }) => {
      set((state) => ({
        activeStreams: {
          ...state.activeStreams,
          [cameraId]: {
            ...state.activeStreams[cameraId],
            status,
            fps,
            resolution,
            ...(codec && { codec }),
            ...(source && { source }),
          },
        },
      }));
    });

    // Handle stream stats updates (periodic compression stats)
    liveSocket.on('stream:stats', ({ cameraId, stats }) => {
      set((state) => ({
        activeStreams: {
          ...state.activeStreams,
          [cameraId]: {
            ...state.activeStreams[cameraId],
            stats,
          },
        },
      }));
    });

    // Handle stream errors
    liveSocket.on('stream:error', ({ cameraId, message }) => {
      console.error(`Stream error for camera ${cameraId}:`, message);
      set((state) => ({
        activeStreams: {
          ...state.activeStreams,
          [cameraId]: {
            ...state.activeStreams[cameraId],
            status: 'error',
            error: message,
          },
        },
        streamErrors: {
          ...state.streamErrors,
          [cameraId]: message,
        },
      }));
    });

    // Handle snapshot received
    liveSocket.on('snapshot:received', ({ cameraId, snapshot, timestamp }) => {
      set((state) => ({
        activeStreams: {
          ...state.activeStreams,
          [cameraId]: {
            ...state.activeStreams[cameraId],
            snapshot: `data:image/jpeg;base64,${snapshot}`,
            snapshotTimestamp: timestamp,
          },
        },
      }));
    });

    liveSocket.on('snapshot:error', ({ message }) => {
      console.error('Snapshot error:', message);
    });

    // Handle real-time detection data (when annotated mode is on)
    liveSocket.on('stream:detections', (data) => {
      const { cameraId, ...detections } = data;
      set((state) => ({
        activeStreams: {
          ...state.activeStreams,
          [cameraId]: {
            ...state.activeStreams[cameraId],
            detections,
          },
        },
      }));
    });
  },

  disconnect: () => {
    if (liveSocket) {
      liveSocket.disconnect();
      liveSocket = null;
    }
    set({ connected: false, activeStreams: {} });
  },

  subscribeCamera: async (cameraId, options = {}) => {
    const { quality = 'medium', fps = 10, deviceId, annotated = false } = options;

    // Ensure Socket.IO connection is ready
    if (!liveSocket?.connected) {
      get().connect();
      // Wait for connection to establish
      await new Promise((resolve, reject) => {
        let attempts = 0;
        const MAX_ATTEMPTS = 50; // 5 seconds max
        const checkConnection = () => {
          if (liveSocket?.connected) {
            resolve();
          } else if (attempts >= MAX_ATTEMPTS) {
            reject(new Error('Live stream connection timeout'));
          } else {
            attempts++;
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
      });
    }

    // Initialize stream state
    set((state) => ({
      activeStreams: {
        ...state.activeStreams,
        [cameraId]: {
          subscribed: false,
          status: 'connecting',
          frame: null,
          quality,
          fps,
        },
      },
    }));

    // Use Socket.IO PRIMARY channel for stream subscription
    if (!liveSocket?.connected) {
      set((state) => ({
        activeStreams: {
          ...state.activeStreams,
          [cameraId]: {
            ...state.activeStreams[cameraId],
            status: 'error',
            error: 'Socket.IO not connected',
          },
        },
      }));
      return;
    }

    // Send stream subscription via Socket.IO (PRIMARY)
    console.log(`✓ Stream subscribe request sent via Socket.IO (PRIMARY) for camera ${cameraId}`);
    liveSocket.emit('stream:subscribe', {
      cameraId,
      quality,
      fps,
      deviceId,
      annotated,
    });
  },

  unsubscribeCamera: async (cameraId, deviceId) => {
    // Use Socket.IO PRIMARY channel for stream unsubscription
    if (liveSocket?.connected) {
      console.log(`✓ Stream unsubscribe request sent via Socket.IO (PRIMARY) for camera ${cameraId}`);
      liveSocket.emit('stream:unsubscribe', { cameraId });
    }

    set((state) => {
      const newStreams = { ...state.activeStreams };
      delete newStreams[cameraId];
      return { activeStreams: newStreams };
    });
  },

  requestSnapshot: async (cameraId, annotated = false, deviceId) => {
    // Use Socket.IO PRIMARY channel for snapshot requests
    if (liveSocket?.connected) {
      console.log(`✓ Snapshot request sent via Socket.IO (PRIMARY) for camera ${cameraId}`);
      liveSocket.emit('snapshot:request', { cameraId, annotated });
    }
  },

  setAnnotated: (cameraId, enabled) => {
    if (liveSocket?.connected) {
      liveSocket.emit('stream:set_annotated', { cameraId, annotated: enabled });
    }
    // Clear detections when turning off
    if (!enabled) {
      set((state) => ({
        activeStreams: {
          ...state.activeStreams,
          [cameraId]: {
            ...state.activeStreams[cameraId],
            detections: null,
          },
        },
      }));
    }
  },

  getStreamState: (cameraId) => {
    return get().activeStreams[cameraId] || null;
  },

  isStreaming: (cameraId) => {
    const stream = get().activeStreams[cameraId];
    return stream?.status === 'streaming';
  },
}));
